import React, { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { Preferences } from '@capacitor/preferences'
import { DeviceTabletIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { isNative, getManagedSecret } from '../services/native'

function DeviceRegistration() {
  const { registerPinDevice } = useAuth()
  const [code, setCode] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [configLoading, setConfigLoading] = useState(true)
  const [requireManaged, setRequireManaged] = useState(false)
  const [blockedOnWeb, setBlockedOnWeb] = useState(false)
  const [autoRegistering, setAutoRegistering] = useState(false)

  // Check if managed device is required, and auto-register if native + managed
  useEffect(() => {
    const checkConfig = async () => {
      try {
        const res = await fetch('/api/auth/pin/config')
        const data = await res.json()
        if (data.success && data.requireManagedDevice) {
          setRequireManaged(true)
          if (!isNative()) {
            setBlockedOnWeb(true)
          } else {
            // Native + managed: attempt auto-register
            setAutoRegistering(true)
            try {
              const secret = await getManagedSecret()
              if (secret) {
                const autoRes = await fetch('/api/auth/pin/auto-register', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ managedSecret: secret })
                })
                const autoData = await autoRes.json()
                if (autoData.success) {
                  localStorage.setItem('pinDeviceToken', autoData.deviceToken)
                  document.cookie = `pinDeviceToken=${autoData.deviceToken}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Strict; Secure`
                  if (isNative()) Preferences.set({ key: 'pinDeviceToken', value: autoData.deviceToken }).catch(() => {})
                  setSuccess(true)
                  setTimeout(() => { window.location.href = '/' }, 1500)
                  return
                } else {
                  setError(autoData.error || 'Auto-registration failed')
                }
              } else {
                setError('This device is not managed. Ensure it is enrolled in your MDM with the Scenelight app configuration.')
              }
            } catch (err) {
              setError('Auto-registration failed. Please try again.')
            } finally {
              setAutoRegistering(false)
            }
          }
        }
      } catch (err) {
        console.log('Failed to check pin config:', err)
      } finally {
        setConfigLoading(false)
      }
    }
    checkConfig()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (code.length < 8) return

    setLoading(true)
    setError(null)

    // If managed device required, read the secret from managed config
    let managedSecretValue = null
    if (requireManaged) {
      managedSecretValue = await getManagedSecret()
      if (!managedSecretValue) {
        setError('This device is not managed. Registration requires the Scenelight app on a managed device.')
        setLoading(false)
        return
      }
    }

    const result = await registerPinDevice(code.trim().toUpperCase(), managedSecretValue)

    if (result.success) {
      setSuccess(true)
      setTimeout(() => {
        window.location.href = '/'
      }, 1500)
    } else {
      setError(result.error)
    }
    setLoading(false)
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center safe-screen">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/images/scenelight-logo.png" alt="Scenelight" className="w-28 h-28 mx-auto" />
          <h1 className="text-4xl text-white mt-4 tracking-widest" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>SCENELIGHT</h1>
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          {configLoading || autoRegistering ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin mx-auto" />
              {autoRegistering && <p className="text-slate-400 text-sm mt-4">Registering device...</p>}
            </div>
          ) : success ? (
            <div className="text-center py-4">
              <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Device Registered</h2>
              <p className="text-slate-400">Redirecting to quick login...</p>
            </div>
          ) : blockedOnWeb ? (
            <div className="text-center py-4">
              <ExclamationTriangleIcon className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Managed Device Required</h2>
              <p className="text-slate-400 text-sm mt-2">
                Device registration requires the Scenelight app on a managed device. Open the Scenelight app on your station iPad to register.
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <DeviceTabletIcon className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                <h2 className="text-xl font-semibold text-white">Register Station Device</h2>
                <p className="text-slate-400 text-sm mt-2">
                  Enter the registration code from your admin to enable quick PIN login on this device.
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Registration Code</label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                    placeholder="Enter 8-character code"
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-center text-xl tracking-[0.2em] font-mono"
                    maxLength={8}
                    autoFocus
                    autoComplete="off"
                  />
                </div>

                <button
                  type="submit"
                  disabled={code.length < 8 || loading}
                  className="w-full px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium disabled:opacity-50 transition-colors"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Registering...
                    </span>
                  ) : (
                    'Register Device'
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Back link */}
        <div className="mt-6 text-center">
          <a href="/login" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
            Back to login
          </a>
        </div>

        <p className="mt-8 text-center text-xs text-slate-600">
          &copy; 2026 Scenelight
        </p>
      </div>
    </div>
  )
}

export default DeviceRegistration
